import { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { createDossier } from "../../../redux/features/dossiers/dossierSlice";
import { getClients } from "../../../redux/features/clients/clientSlice";
import { getUsers } from "../../../redux/features/users/userSlice";
import { getSchoolYears } from "../../../redux/features/schoolYears/schoolYearSlice";

import SearchSelect from "../../ui/searchSelect/SearchSelect";

import "./DossierForm.css";

const STEP_OPTIONS = [
  { value: "aanmelding", label: "Aanmelding" },
  { value: "administratie", label: "Administratie" },
  { value: "begeleiding", label: "Begeleiding" },
  { value: "vdab", label: "VDAB" },
  { value: "kld", label: "KLD" },
  { value: "afronding", label: "Afronding" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Actief" },
  { value: "paused", label: "Gepauzeerd" },
  { value: "completed", label: "Afgerond" },
];

const initialState = {
  client: "",
  school_year: "",
  coach: "",
  status: "active",
  current_step: "aanmelding",
};

export default function DossierForm({ onSuccess, onCancel }) {
  const dispatch = useDispatch();

  const { clients } = useSelector((state) => state.clients);
  const { users } = useSelector((state) => state.users);
  const { schoolYears } = useSelector((state) => state.schoolYears);
  const { isCreating, error } = useSelector((state) => state.dossiers);

  const [formData, setFormData] = useState(initialState);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    dispatch(
      getClients({
        limit: 100,
        is_active: "true",
      }),
    );

    dispatch(
      getUsers({
        limit: 100,
        status: "ACTIVE",
      }),
    );

    dispatch(getSchoolYears());
  }, [dispatch]);

  const clientOptions = useMemo(() => {
    return clients.map((client) => ({
      value: client._id,
      label:
        client.display_name ||
        `${client.first_name || ""} ${client.last_name || ""}`.trim(),
    }));
  }, [clients]);

  const coachOptions = useMemo(() => {
    return users
      .filter((user) =>
        ["POWER_INTERNAL_COACH", "POWER_EXTERNAL_COACH"].includes(user.role),
      )
      .map((user) => ({
        value: user._id,
        label: `${user.first_name} ${user.last_name} — ${user.email}`,
      }));
  }, [users]);

  const schoolYearOptions = useMemo(() => {
    return schoolYears.map((schoolYear) => ({
      value: schoolYear._id,
      label: `${schoolYear.name}${schoolYear.is_active ? " — actief" : ""}`,
    }));
  }, [schoolYears]);

  const setFieldValue = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setValidationErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFieldValue(name, value);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.client) {
      errors.client = "Cliënt is verplicht.";
    }

    if (!formData.school_year) {
      errors.school_year = "Schooljaar is verplicht.";
    }

    if (!formData.coach) {
      errors.coach = "Coach is verplicht.";
    }

    if (!formData.status) {
      errors.status = "Status is verplicht.";
    }

    if (!formData.current_step) {
      errors.current_step = "Stap is verplicht.";
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => ({
    client: formData.client,
    school_year: formData.school_year,
    coach: formData.coach,
    status: formData.status,
    current_step: formData.current_step,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      await dispatch(createDossier(buildPayload())).unwrap();

      onSuccess?.();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form className="dossier-form" onSubmit={handleSubmit}>
      {error && <div className="dossier-form__error">{error}</div>}

      <div className="dossier-form__notice">
        Dit formulier maakt alleen het dossier aan. De dynamische dossierwaarden
        worden later ingevuld op de detailpagina.
      </div>

      <div className="dossier-form__grid">
        <div className="dossier-form__group dossier-form__group--full">
          <SearchSelect
            label="Cliënt *"
            value={formData.client}
            options={clientOptions}
            onChange={(value) => setFieldValue("client", value)}
            placeholder="Zoek cliënt..."
            disabled={isCreating}
            error={validationErrors.client}
          />
        </div>

        <div className="dossier-form__group dossier-form__group--full">
          <SearchSelect
            label="Coach *"
            value={formData.coach}
            options={coachOptions}
            onChange={(value) => setFieldValue("coach", value)}
            placeholder="Zoek coach..."
            disabled={isCreating}
            error={validationErrors.coach}
          />
        </div>

        <div className="dossier-form__group">
          <label>Schooljaar *</label>

          <select
            name="school_year"
            value={formData.school_year}
            onChange={handleChange}
            disabled={isCreating}
            className={
              validationErrors.school_year ? "dossier-form__input--error" : ""
            }
          >
            <option value="">Selecteer schooljaar</option>

            {schoolYearOptions.map((schoolYear) => (
              <option key={schoolYear.value} value={schoolYear.value}>
                {schoolYear.label}
              </option>
            ))}
          </select>

          {validationErrors.school_year && (
            <span>{validationErrors.school_year}</span>
          )}
        </div>

        <div className="dossier-form__group">
          <label>Status *</label>

          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            disabled={isCreating}
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div className="dossier-form__group dossier-form__group--full">
          <label>Start stap *</label>

          <select
            name="current_step"
            value={formData.current_step}
            onChange={handleChange}
            disabled={isCreating}
          >
            {STEP_OPTIONS.map((step) => (
              <option key={step.value} value={step.value}>
                {step.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="dossier-form__actions">
        <button
          type="button"
          className="dossier-form__cancel"
          onClick={onCancel}
          disabled={isCreating}
        >
          Annuleren
        </button>

        <button
          type="submit"
          className="dossier-form__submit"
          disabled={isCreating}
        >
          {isCreating ? "Aanmaken..." : "Dossier aanmaken"}
        </button>
      </div>
    </form>
  );
}
